import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import useClickOutside from '../../hooks/useClickOutside';
import useData from '../../hooks/useData';
import { TaskIcon, ProjectIcon, GroupIcon, AddIcon } from '../ui/Icons';

const ResultItem = ({ item, isActive, onMouseEnter, onClick }) => {
    const getIcon = () => {
        switch (item.type) {
            case 'task': return <TaskIcon size={16} />;
            case 'project': return <ProjectIcon size={16} />;
            case 'client': return <GroupIcon size={16} />;
            case 'action': return <AddIcon size={16} />;
            default: return null;
        }
    };

    return (
        <li
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            className={`p-3 text-white cursor-pointer flex items-center gap-3 transition-colors ${isActive ? 'bg-white/10' : ''}`}
        >
            <div className="bg-white/10 p-1.5 rounded-md">{getIcon()}</div>
            <div>
                <p className="text-sm font-medium">{item.label}</p>
                {item.subtitle && <p className="text-xs text-gray-400">{item.subtitle}</p>}
            </div>
        </li>
    );
};

const CommandPalette = ({ isOpen, onClose, onSelectCommand }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const paletteRef = useRef(null);
    const inputRef = useRef(null);
    const { tasks, projects, clients } = useData();

    const searchResults = useMemo(() => {
        const lowerCaseTerm = searchTerm.toLowerCase();
        if (!lowerCaseTerm) return [];

        const results = {};

        // Search Tasks
        const matchedTasks = tasks.filter(t => !t.isArchived && t.title.toLowerCase().includes(lowerCaseTerm));
        if (matchedTasks.length > 0) {
            results['Tarefas'] = matchedTasks.map(t => ({
                id: `task-${t.id}`,
                label: t.title,
                subtitle: projects.find(p => p.id === t.projectId)?.name || t.client,
                action: 'open',
                type: 'task',
                item: t
            }));
        }

        // Search Projects
        const matchedProjects = projects.filter(p => p.name.toLowerCase().includes(lowerCaseTerm));
        if (matchedProjects.length > 0) {
            results['Projetos'] = matchedProjects.map(p => ({
                id: `project-${p.id}`,
                label: p.name,
                subtitle: clients.find(c => c.id === p.clientId)?.name,
                action: 'open',
                type: 'project',
                item: p
            }));
        }

        // Search Clients
        const matchedClients = clients.filter(c => c.name.toLowerCase().includes(lowerCaseTerm));
        if (matchedClients.length > 0) {
            results['Clientes'] = matchedClients.map(c => ({
                id: `client-${c.id}`,
                label: c.name,
                action: 'open',
                type: 'client',
                item: c
            }));
        }

        // Search Actions
        const commands = [
            { id: 'new-task', label: 'Criar nova tarefa', action: 'newTask', type: 'action' },
            { id: 'go-dashboard', label: 'Ir para Dashboard', action: 'navigate', destination: 'dashboard', type: 'action' },
            { id: 'go-flow', label: 'Ir para O Fluxo', action: 'navigate', destination: 'flow', type: 'action' },
            { id: 'go-projects', label: 'Ir para Projetos', action: 'navigate', destination: 'projects', type: 'action' },
            { id: 'go-clients', label: 'Ir para Clientes', action: 'navigate', destination: 'clients', type: 'action' },
        ];
        const matchedActions = commands.filter(cmd => cmd.label.toLowerCase().includes(lowerCaseTerm));
        if (matchedActions.length > 0) {
            results['Ações'] = matchedActions;
        }

        return results;

    }, [searchTerm, tasks, projects, clients]);

    const flatResults = useMemo(() => Object.values(searchResults).flat(), [searchResults]);

    useClickOutside(paletteRef, onClose);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        } else {
            setSearchTerm('');
            setActiveIndex(0);
        }
    }, [isOpen]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prevIndex) => (prevIndex + 1) % flatResults.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prevIndex) => (prevIndex - 1 + flatResults.length) % flatResults.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (flatResults[activeIndex]) {
                onSelectCommand(flatResults[activeIndex]);
            }
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center pt-20 z-50">
            <div ref={paletteRef} className="w-full max-w-lg bg-gray-800/80 backdrop-blur-lg border border-white/20 rounded-xl shadow-2xl" onKeyDown={handleKeyDown}>
                <div className="p-4 border-b border-white/10">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Pesquisar tarefas, projetos, clientes..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setActiveIndex(0); }}
                        className="w-full bg-transparent text-white placeholder-gray-400 focus:outline-none"
                    />
                </div>
                <ul className="max-h-96 overflow-y-auto">
                    {Object.keys(searchResults).length > 0 ? (
                        Object.entries(searchResults).map(([category, items]) => (
                            <div key={category}>
                                <h3 className="text-xs font-bold uppercase text-gray-400 px-3 pt-3 pb-1">{category}</h3>
                                {items.map((item) => (
                                    <ResultItem
                                        key={item.id}
                                        item={item}
                                        isActive={flatResults[activeIndex]?.id === item.id}
                                        onMouseEnter={() => setActiveIndex(flatResults.findIndex(r => r.id === item.id))}
                                        onClick={() => onSelectCommand(item)}
                                    />
                                ))}
                            </div>
                        ))
                    ) : (
                        searchTerm && <li className="p-4 text-gray-400">Nenhum resultado encontrado.</li>
                    )}
                </ul>
            </div>
        </div>,
        document.body
    );
};

export default CommandPalette;